/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Encryption pane. Pinned to Figma node 3542:5638 ("Encryption"):
//
//   * "Always on" badge — green pill (#3CCB4F at 12% opacity) +
//     ShieldCheck glyph.
//   * Device list      — same card pattern as the Sessions current-
//     device card (8px padding, 8px radius, fill #11131A border
//     #22242A). Fingerprint shown as monospace truncated head…tail.

import { ShieldCheck, Smartphone } from "lucide-react-native";
import { useEffect, useState } from "react";
import { Text, View } from "react-native";
import { SettingsDivider, SettingsRow } from "../SettingsRow";

export interface EncryptionDevice {
	readonly deviceId: string;
	readonly signaturePublicKeyRef: string;
}

export interface EncryptionPaneProps {
	readonly loadDevices: () => Promise<{
		readonly currentDeviceId: string | null;
		readonly devices: readonly EncryptionDevice[];
	}>;
}

export function EncryptionPane(props: EncryptionPaneProps): JSX.Element {
	const { loadDevices } = props;
	const [devices, setDevices] = useState<readonly EncryptionDevice[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			try {
				const result = await loadDevices();
				if (cancelled) return;
				setCurrentDeviceId(result.currentDeviceId);
				setDevices(result.devices);
			} catch (err) {
				if (!cancelled) setError((err as Error).message);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [loadDevices]);

	return (
		<View className="flex flex-col gap-4">
			<SettingsRow
				variant="hero"
				title="Encryption"
				description="Every conversation on Ping is end-to-end encrypted."
				testID="settings-encryption-overview"
			/>
			<SettingsRow
				title="End-to-end encryption"
				description="Every conversation on Ping is encrypted with the MLS protocol. Ping cannot read the contents of your messages."
				testID="settings-encryption-end-to-end"
			>
				<View
					className="
						inline-flex flex-row items-center gap-2 self-start
						px-3 py-1.5 rounded-full
						bg-[#3CCB4F]/[0.12]
					"
				>
					<ShieldCheck size={16} strokeWidth={1.75} color="#3CCB4F" />
					<Text className="text-[13px] leading-[18px] font-semibold text-[#3CCB4F]">
						Always on
					</Text>
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Your devices"
				description="Devices currently registered to your account. Each owns its own signing key."
				testID="settings-encryption-devices"
			>
				{loading && (
					<Text className="text-[11px] leading-[15px] text-light-text-tertiary dark:text-[#6B7280]">
						Loading…
					</Text>
				)}
				{error != null && (
					<Text className="text-[12px] leading-[16px] text-[#FF5353]">
						{error}
					</Text>
				)}
				{!loading && error == null && devices.length === 0 && (
					<Text className="text-[11px] leading-[15px] text-light-text-tertiary dark:text-[#6B7280]">
						No devices registered yet.
					</Text>
				)}
				<View className="flex flex-col gap-2">
					{devices.map((d) => {
						const isCurrent = d.deviceId === currentDeviceId;
						const fingerprintHead = d.signaturePublicKeyRef.slice(0, 8);
						const fingerprintTail = d.signaturePublicKeyRef.slice(-8);
						return (
							<View
								key={d.deviceId}
								className="
									flex flex-row items-center gap-4
									px-2 py-2 rounded-lg
									bg-light-surface-primary dark:bg-[#11131A] border border-light-border-secondary dark:border-[#22242A]
								"
							>
								<View
									className="
										w-9 h-9 rounded-lg
										bg-light-surface-secondary dark:bg-[#15171C]
										flex items-center justify-center
									"
								>
									<Smartphone size={18} strokeWidth={1.75} color="#B5BAC3" />
								</View>
								<View className="min-w-0 flex-1">
									<View className="flex flex-row items-center gap-2">
										<Text className="text-[14px] leading-[20px] font-semibold text-light-text-primary dark:text-[#F5F6F8]">
											{isCurrent ? "This device" : "Device"}
										</Text>
										{isCurrent && (
											<View className="flex flex-row items-center gap-1">
												<View className="w-1.5 h-1.5 rounded-full bg-[#3CCB4F]" />
												<Text className="text-[11px] leading-[15px] font-normal text-[#3CCB4F]">
													Active
												</Text>
											</View>
										)}
									</View>
									<Text className="font-mono text-[11px] leading-[15px] text-light-text-secondary dark:text-[#B5BAC3]">
										{fingerprintHead}…{fingerprintTail}
									</Text>
								</View>
							</View>
						);
					})}
				</View>
			</SettingsRow>
		</View>
	);
}
